import { LoadingOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import Image from 'next/image';
import React from 'react';
import BlurHashImage from '../../../../BlurHashImage';
import Spinner from '../../../../Spinner';
import { getLiveVideoLabels } from './liveVideoLabels';
import { useLiveVideo } from './LiveVideoProvider';

const { Title } = Typography;

// LiveEndedState Component - Shows when live video has ended or is in post-live processing
// it can have two states:
// - VIDEO_ENDED: Live stream has ended
// - POST_LIVE_PENDING: Video is being processed for VOD

export const LIVE_ENDED_STATE = {
  VIDEO_ENDED: 'VIDEO_ENDED',
  POST_LIVE_PENDING: 'POST_LIVE_PENDING',
  LIVE_PAUSED: 'LIVE_PAUSED'
};

export default function LiveEndedState({
  state = LIVE_ENDED_STATE.VIDEO_ENDED,
  onWatchAgain,
  uiLabels = {}
}) {
  const { enabled, video } = useLiveVideo();
  const liveLabels = getLiveVideoLabels(uiLabels);

  if (!enabled) {
    return null;
  }

  const getTitle = () => {
    if (state === LIVE_ENDED_STATE.VIDEO_ENDED) {
      return liveLabels.STREAM_ENDED_TITLE;
    }
    if (state === LIVE_ENDED_STATE.LIVE_PAUSED) {
      return liveLabels.LIVE_VIDEO_PAUSED_TITLE;
    }
    return liveLabels.REPLAY_COMING_SOON_TITLE;
  };

  const getSubtitle = () => {
    if (state === LIVE_ENDED_STATE.POST_LIVE_PENDING) {
      return liveLabels.PROCESSING_MESSAGE_TEXT;
    }
    if (state === LIVE_ENDED_STATE.LIVE_PAUSED) {
      return liveLabels.LIVE_VIDEO_PAUSED_SUBTITLE;
    }
    return liveLabels.THANK_YOU_TEXT;
  };

  return (
    <div className="live-ended-container">
      {/* Blur background image */}
      <div className="live-ended-blur-bg">
        <BlurHashImage src={video?.thumb} alt={video?.title} fill priority />
      </div>

      <div className="live-ended-overlay">
        <div className="live-ended-content">
          <div className="live-ended-header">
            {state === LIVE_ENDED_STATE.VIDEO_ENDED && (
              <div className="checkmark-icon">
                <Image
                  src="/images/outlined-checkmark.svg"
                  alt="Stream ended"
                  width={80}
                  height={80}
                />
              </div>
            )}
            <Title level={5} className="live-ended-title">
              {getTitle()}
            </Title>
          </div>

          <div className="live-ended-processing">
            {state === LIVE_ENDED_STATE.POST_LIVE_PENDING && (
              <div className="processing-spinner">
                <Spinner indicator={<LoadingOutlined spin />} />
              </div>
            )}
            <span className="processing-text">{getSubtitle()}</span>
          </div>

          {state === LIVE_ENDED_STATE.VIDEO_ENDED && (
            <div className="live-ended-actions">
              <Button
                className="cs-button w-full"
                type="primary"
                size="large"
                onClick={onWatchAgain}
              >
                {liveLabels.WATCH_REPLAY_BUTTON}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
